import { useQuery } from 'react-query';

import { useResolvedLanguage } from '~/features/internationalization/hooks/use-resolved-language';

import { Program } from '../models/program';
import { ProgramMetadata } from '../models/program-metadata';
import { fetchLocalizedProgram } from '../utils/fetch-localized-program';

type UseLocalizedProgramOptions = {
  enabled?: boolean;
  initialLanguage: string;
  initialProgram?: Program | null;
  initialProgramMetadata?: ProgramMetadata | null;
  key?: string;
  locale?: string;
  programKeys?: string[];
  version?: string | null;
};

export function useLocalizedProgram({
  enabled = true,
  initialLanguage,
  initialProgram = null,
  initialProgramMetadata = null,
  key,
  locale = 'en-us',
  programKeys,
  version,
}: UseLocalizedProgramOptions) {
  const language = useResolvedLanguage(initialLanguage);
  const shouldFetchLocalizedProgram =
    enabled && language !== initialLanguage && (Boolean(key) || Boolean(programKeys?.length));

  const { data, isFetching } = useQuery({
    queryKey: ['localized-program', key, version, locale, programKeys, language],
    queryFn: () => fetchLocalizedProgram({ key, locale, preferredLanguage: language, programKeys, version }),
    enabled: shouldFetchLocalizedProgram,
    initialData:
      language === initialLanguage
        ? {
            program: initialProgram,
            programMetadata: initialProgramMetadata,
          }
        : undefined,
    keepPreviousData: true,
    refetchOnMount: false,
  });

  return {
    isFetching,
    language,
    program: data?.program ?? initialProgram,
    programMetadata: data?.programMetadata ?? initialProgramMetadata,
  };
}
